import { TIMESHEET_STATUSES, type TimesheetStatus } from '@hr/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { CalendarClockIcon, FileSpreadsheetIcon } from 'lucide-react';
import { z } from 'zod';
import { Combobox } from '@/components/combobox';
import { EmptyState, PageHeader } from '@/components/page';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { usePreview } from '@/features/preview/use-preview';
import { useRegistry } from '@/features/registry/use-registry';
import { MonthPicker } from '@/features/time/month-picker';
import { currentMonth, monthLabel } from '@/lib/months';
import { TimesheetEditor } from '@/features/time/timesheet-editor';
import { TIMESHEET_STATUS_LABELS, uk } from '@/i18n/uk';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';

const searchSchema = z.object({
  month: z
    .string()
    .regex(/^\d{4}-\d{2}$/)
    .optional()
    .catch(undefined),
  project: z.uuid().optional().catch(undefined),
  center: z.uuid().optional().catch(undefined),
  status: z
    .enum([...TIMESHEET_STATUSES, 'none'])
    .optional()
    .catch(undefined),
  employee: z.uuid().optional().catch(undefined),
});

export const Route = createFileRoute('/_app/timesheets')({
  validateSearch: searchSchema,
  component: TimesheetsPage,
});

const STATUS_STYLE: Record<TimesheetStatus, string> = {
  draft: '',
  submitted: 'border-sky-300 text-sky-800 dark:text-sky-200',
  approved: 'border-emerald-300 text-emerald-800 dark:text-emerald-200',
  returned: 'border-amber-300 text-amber-800 dark:text-amber-200',
};

function TimesheetsPage() {
  const openPreview = usePreview();
  const t = uk.time;
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const registry = useRegistry();
  const month = search.month ?? currentMonth();
  const setSearch = (patch: Partial<z.infer<typeof searchSchema>>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const list = useQuery(
    trpc.timesheets.month.queryOptions({
      month,
      projectId: search.project,
      centerId: search.center,
      status: search.status,
    }),
  );
  const selected = list.data?.items.find((i) => i.employeeId === search.employee);

  return (
    <>
      <PageHeader
        title={t.timesheets}
        description={
          list.data &&
          `${t.workingDays}: ${list.data.workingDays} · ${t.norm}: ${list.data.normHours}`
        }
        actions={
          <Button
            variant="outline"
            onClick={() =>
              openPreview({
                url: `/api/timesheets/${month}/export.xlsx${search.project ? `?projectId=${search.project}` : ''}`,
              })
            }
          >
            <FileSpreadsheetIcon /> {t.export}
          </Button>
        }
      />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <MonthPicker value={month} onChange={(m) => setSearch({ month: m })} />
        <Combobox
          className="w-44"
          value={search.project}
          onChange={(v) => setSearch({ project: v ?? undefined })}
          options={(registry.data?.projects ?? []).map((p) => ({ value: p.id, label: p.code }))}
          placeholder={uk.registry.project}
          allowClear
        />
        <Combobox
          className="w-44"
          value={search.center}
          onChange={(v) => setSearch({ center: v ?? undefined })}
          options={(registry.data?.centers ?? []).map((c) => ({ value: c.id, label: c.name }))}
          placeholder={uk.registry.center}
          allowClear
        />
        <Select
          value={search.status ?? 'all'}
          onValueChange={(v) =>
            setSearch({ status: v === 'all' ? undefined : (v as TimesheetStatus | 'none') })
          }
        >
          <SelectTrigger className="w-44" aria-label={uk.registry.status}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {uk.registry.status}: {uk.common.all.toLowerCase()}
            </SelectItem>
            <SelectItem value="none">{t.notStarted}</SelectItem>
            {TIMESHEET_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {TIMESHEET_STATUS_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {list.isPending ? (
        <Skeleton className="h-64" />
      ) : list.data?.items.length === 0 ? (
        <EmptyState icon={CalendarClockIcon} title={t.emptyMonth} />
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-medium">{uk.personnel.fullName}</th>
                <th className="px-3 py-2 text-right font-medium">{t.planned}</th>
                <th className="px-3 py-2 text-right font-medium">{t.actual}</th>
                <th className="px-3 py-2 text-right font-medium">{t.absenceDays}</th>
                <th className="px-3 py-2 text-left font-medium">{uk.registry.status}</th>
              </tr>
            </thead>
            <tbody>
              {list.data?.items.map((i) => (
                <tr
                  key={i.employeeId}
                  className="cursor-pointer border-t hover:bg-muted/40"
                  onClick={() => setSearch({ employee: i.employeeId })}
                >
                  <td className="px-3 py-2 font-medium">{i.name}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{i.planned}</td>
                  <td
                    className={cn(
                      'px-3 py-2 text-right tabular-nums',
                      i.status && i.actual > i.planned && 'text-amber-700 dark:text-amber-300',
                    )}
                  >
                    {i.status ? i.actual : '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{i.absenceDays || ''}</td>
                  <td className="px-3 py-2">
                    {i.status ? (
                      <Badge variant="outline" className={STATUS_STYLE[i.status]}>
                        {TIMESHEET_STATUS_LABELS[i.status]}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">{t.notStarted}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Sheet
        open={Boolean(search.employee)}
        onOpenChange={(o) => !o && setSearch({ employee: undefined })}
      >
        <SheetContent className="w-full overflow-y-auto sm:max-w-[min(96vw,110rem)]">
          <SheetHeader>
            <SheetTitle>
              {t.timesheet}: {selected?.name}
            </SheetTitle>
            <SheetDescription>{monthLabel(month)}</SheetDescription>
          </SheetHeader>
          <div className="px-4 pb-4">
            {search.employee && <TimesheetEditor employeeId={search.employee} month={month} />}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
